import { LockClosedIcon } from '@heroicons/react/24/solid';
import { Link, useRouteLoaderData } from '@remix-run/react';
import cx from 'classnames';
import { useTranslation } from 'react-i18next';

import { SubscriberGate } from '~/components/Actions/types';
import Signup from '~/components/Signup';
import Text from '~/components/Text';

import type { Publication } from '~/interfaces/publication';

interface GatedContentProps {
  title: string;
  message: string;
  cta: string;
  publication: Publication;
  createActionType?: SubscriberGate;
  theme?: 'subscribe' | 'subscribe-gate' | 'subscribe-gate-bottom' | 'content' | 'navs' | 'default' | 'primary';
  postSlug: string;
}

const FreeGatedContent = ({
  title,
  message,
  cta,
  publication,
  theme,
  createActionType = SubscriberGate.GATED,
  postSlug,
}: GatedContentProps) => {
  const { signupWidgetConfigs } = useRouteLoaderData<any>('root');
  const { t } = useTranslation();
  const backgroundColor = publication.web_template.web_theme.color_background;
  const bodyColor = publication.web_template.web_theme.color_body;

  return (
    <div
      className={cx(
        'mx-auto flex w-full flex-col items-center px-10',
        {
          'mb-10 bg-wt-text-on-background py-10 text-wt-background': theme === 'subscribe-gate',
        },
        {
          'max-w-xl bg-transparent': theme === 'subscribe-gate-bottom',
        },
        { 'max-w-2xl': theme !== 'subscribe-gate-bottom' },
      )}
    >
      {publication.private && <LockClosedIcon className="h-16 w-16" />}

      <div className="flex flex-col space-y-2">
        <Text
          as="h2"
          type="title"
          size={theme === 'subscribe-gate-bottom' ? '2xl' : '3xl'}
          theme={theme}
          className="text-center text-3xl font-bold leading-tight tracking-normal"
        >
          {title}
        </Text>
        <Text
          as="p"
          type="body"
          size={theme === 'subscribe-gate-bottom' ? 'md' : 'lg'}
          theme={theme}
          className="text-center leading-tight tracking-normal"
        >
          {message}
        </Text>
      </div>
      <div className="w-full py-8">
        <Signup
          signupStyles={{
            ...signupWidgetConfigs,
            buttonText: cta,
            fontSize: theme === 'subscribe-gate-bottom' && 'sm',
            bodyTextColor: theme === 'subscribe-gate' ? backgroundColor : bodyColor,
          }}
          type={createActionType}
        />
      </div>
      <div className="pb-2">
        <Text as="span" type="body" theme={theme} className="flex items-center justify-center text-center text-sm">
          {t('login.already_a_subscriber')}
          <Link to={`/login?postSlug=${postSlug}`} className="ml-1">
            <span
              className={cx('underline underline-offset-1', {
                'text-wt-text-on-background': theme !== 'subscribe-gate',
              })}
            >
              {t('shared.cta.sign_in')}
            </span>
            .
          </Link>
        </Text>
        <Link to="/" className="cursor-pointer">
          <Text as="span" type="body" theme={theme} className="flex justify-center py-2 text-center text-sm sm:text-lg">
            <span className="text-sm underline underline-offset-1">{t('login.not_now')}</span>
          </Text>
        </Link>
      </div>
    </div>
  );
};

export default FreeGatedContent;
